/**
 * Line-level text diff for the agent chat's edited-file cards.
 *
 * Dependency-free and platform-neutral: the agent proxy (Node) computes the
 * payload and the browser panel renders it, so nothing here may touch the DOM
 * or `node:*`.
 */

const DEFAULT_CONTEXT = 2;
const DEFAULT_MAX_LINES = 200;
const DEFAULT_MAX_LINE_LENGTH = 300;

// Above this the quadratic LCS table stops being worth its memory; the diff
// degrades to "replaced wholesale" rather than stalling a turn.
const MAX_LCS_CELLS = 250_000;
const MAX_LCS_SIDE = 2_000;

/**
 * Length-cap one diff line. Unlike the service's `truncate`, whitespace is
 * preserved verbatim — collapsing it would destroy code indentation.
 */
export function truncateDiffLine(value, max = DEFAULT_MAX_LINE_LENGTH) {
  const text = typeof value === 'string' ? value : String(value ?? '');
  return text.length > max ? `${text.slice(0, Math.max(0, max - 1))}…` : text;
}

export function splitLines(value) {
  return String(value ?? '').split(/\r?\n/);
}

/**
 * 1-based line number of `needle`'s first character inside `haystack`, or null
 * when it does not occur. Anchors a hunk to its place in the real file.
 */
export function lineNumberOf(haystack, needle) {
  if (typeof haystack !== 'string' || typeof needle !== 'string' || !needle) return null;
  const at = haystack.indexOf(needle);
  if (at < 0) return null;
  let line = 1;
  for (let index = 0; index < at; index++) {
    if (haystack[index] === '\n') line++;
  }
  return line;
}

/** Count of `needle` occurrences in `haystack` (0 when either is empty). */
export function countOccurrences(haystack, needle) {
  if (typeof haystack !== 'string' || typeof needle !== 'string' || !needle) return 0;
  return haystack.split(needle).length - 1;
}

/**
 * Ops for one changed region, deletions before insertions so the result reads
 * like a unified diff.
 */
function replaceOps(oldLines, newLines) {
  const ops = [];
  for (const text of oldLines) ops.push({ kind: '-', text });
  for (const text of newLines) ops.push({ kind: '+', text });
  return ops;
}

function lcsOps(oldLines, newLines) {
  const n = oldLines.length;
  const m = newLines.length;
  // dp[i][j] = LCS length of oldLines[i..] and newLines[j..], flattened.
  const width = m + 1;
  const dp = new Int32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * width + j] =
        oldLines[i] === newLines[j]
          ? dp[(i + 1) * width + j + 1] + 1
          : Math.max(dp[(i + 1) * width + j], dp[i * width + j + 1]);
    }
  }

  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldLines[i] === newLines[j]) {
      ops.push({ kind: ' ', text: oldLines[i] });
      i++;
      j++;
    } else if (dp[(i + 1) * width + j] >= dp[i * width + j + 1]) {
      ops.push({ kind: '-', text: oldLines[i] });
      i++;
    } else {
      ops.push({ kind: '+', text: newLines[j] });
      j++;
    }
  }
  while (i < n) ops.push({ kind: '-', text: oldLines[i++] });
  while (j < m) ops.push({ kind: '+', text: newLines[j++] });
  return ops;
}

/**
 * Diff two texts by line.
 *
 * `added`/`removed` are the true totals even when the emitted hunks are capped,
 * so a truncated payload still reports honest counts. Hunk starts are 1-based
 * and shifted by `oldOffset`/`newOffset`, letting a caller that diffed a slice
 * of a file report absolute line numbers.
 */
export function diffLines(
  oldText,
  newText,
  {
    context = DEFAULT_CONTEXT,
    maxLines = DEFAULT_MAX_LINES,
    maxLineLength = DEFAULT_MAX_LINE_LENGTH,
    oldOffset = 0,
    newOffset = 0
  } = {}
) {
  const oldLines = splitLines(oldText);
  const newLines = splitLines(newText);

  if (oldText === newText) {
    return { added: 0, removed: 0, hunks: [], truncated: false };
  }

  // Identical head/tail lines never need the DP table and are the bulk of a
  // typical edit, so trimming them first is what keeps large files tractable.
  let head = 0;
  const maxHead = Math.min(oldLines.length, newLines.length);
  while (head < maxHead && oldLines[head] === newLines[head]) head++;
  let tail = 0;
  const maxTail = Math.min(oldLines.length, newLines.length) - head;
  while (
    tail < maxTail &&
    oldLines[oldLines.length - 1 - tail] === newLines[newLines.length - 1 - tail]
  )
    tail++;

  const midOld = oldLines.slice(head, oldLines.length - tail);
  const midNew = newLines.slice(head, newLines.length - tail);

  const tooBig =
    midOld.length > MAX_LCS_SIDE ||
    midNew.length > MAX_LCS_SIDE ||
    midOld.length * midNew.length > MAX_LCS_CELLS;
  const midOps = tooBig ? replaceOps(midOld, midNew) : lcsOps(midOld, midNew);

  const ops = [];
  for (let index = 0; index < head; index++) ops.push({ kind: ' ', text: oldLines[index] });
  ops.push(...midOps);
  for (let index = oldLines.length - tail; index < oldLines.length; index++) {
    ops.push({ kind: ' ', text: oldLines[index] });
  }

  // Cursor = the line number this op sits at on each side, tracked before the
  // op is consumed so a hunk starting on an insertion still knows where it
  // lands in the old file.
  let oldCursor = 1;
  let newCursor = 1;
  let added = 0;
  let removed = 0;
  for (const op of ops) {
    op.oldCursor = oldCursor;
    op.newCursor = newCursor;
    if (op.kind === '+') {
      added++;
      newCursor++;
    } else if (op.kind === '-') {
      removed++;
      oldCursor++;
    } else {
      oldCursor++;
      newCursor++;
    }
  }

  if (!added && !removed) {
    return { added: 0, removed: 0, hunks: [], truncated: false };
  }

  // Windows of `context` lines around every changed run, merged where they
  // touch so adjacent edits read as one hunk.
  const windows = [];
  for (let index = 0; index < ops.length; index++) {
    if (ops[index].kind === ' ') continue;
    const start = Math.max(0, index - context);
    const end = Math.min(ops.length - 1, index + context);
    const last = windows.at(-1);
    if (last && start <= last.end + 1) last.end = Math.max(last.end, end);
    else windows.push({ start, end });
  }

  const hunks = [];
  let budget = Math.max(1, maxLines);
  let truncated = false;
  for (const window of windows) {
    if (budget <= 0) {
      truncated = true;
      break;
    }
    const slice = ops.slice(window.start, window.end + 1);
    const kept = slice.slice(0, budget);
    if (kept.length < slice.length) truncated = true;
    budget -= kept.length;
    hunks.push({
      oldStart: kept[0].oldCursor + oldOffset,
      newStart: kept[0].newCursor + newOffset,
      lines: kept.map((op) => op.kind + truncateDiffLine(op.text, maxLineLength))
    });
  }

  return { added, removed, hunks, truncated };
}
